<script lang="ts" setup>
import { computed, ref } from "vue";
import { Handle, Position } from "@vue-flow/core";
import { NodeToolbar } from "@vue-flow/node-toolbar";
import { serviceTypeDisplay, type ServiceNodeData } from "@/types";
import { finishedWithNoTransactions, getRunStatusLabel, isStillCounting, serviceCountChips } from "../utils/runFormatters";
import StatusCountChips from "./StatusCountChips.vue";

const props = defineProps<{
  data: ServiceNodeData;
  id: string;
}>();

const emit = defineEmits<{
  nodeClick: [nodeId: string, nodeData: ServiceNodeData];
}>();

const isHovered = ref(false);
let hoverTimeout: NodeJS.Timeout | null = null;
const addServiceLine = ref<HTMLElement>();

// Dynamic handle positions based on flow direction
const inputHandlePosition = computed(() => {
  return props.data.flowDirection === "LR" ? Position.Left : Position.Top;
});

const outputHandlePosition = computed(() => {
  return props.data.flowDirection === "LR" ? Position.Right : Position.Bottom;
});

// Per-type icon from the single service-type display source, so node cards, the editor header, and
// the workflow list stay in sync.
const serviceIcon = computed(() => serviceTypeDisplay(props.data.serviceType).icon);

// Use Bootstrap secondary color for all icons
const nodeColor = computed(() => {
  return "var(--bs-secondary)"; // Use Bootstrap secondary color
});

// Single resolved status: history mode uses run.status, live run uses executionData.status
const nodeStatus = computed(() =>
  props.data.run?.status || props.data.executionData?.status
);

// Run-status icon colour — the shared .run-status-* palette (styles.scss) so the icon matches
// the run-history status badge rather than Bootstrap's vivid text-* tokens.
const statusClass = computed(() => {
  switch (nodeStatus.value) {
    case "Succeeded":
    case "success":
    case "completed":
      return "run-status-succeeded";
    case "Failed":
    case "error":
    case "failed":
      return "run-status-failed";
    case "Warning":
    case "warning":
      return "run-status-warning";
    case "Running":
    case "running":
      return "run-status-running";
    case "Pending":
    case "pending":
    case "skipped":
      return "run-status-pending";
    default:
      return "";
  }
});

// Duration shown bottom-right in history mode. Current executionNote format is
// "completed in X.Xs" — strip the prefix and just show the duration.
const runDuration = computed(() => {
  const note = props.data.executionNote;
  if (!note) return '';
  return note.replace(/^completed in\s+/i, '');
});

function handleNodeClick(event?: Event) {
  emit("nodeClick", props.id, props.data);
}

function handleAddService(event: Event) {
  event.stopPropagation();
  if (props.data.onAddService) {
    props.data.onAddService(props.id);
  }
}

function handleRunService(event: Event) {
  event.stopPropagation();
  if (props.data.onRunService) {
    props.data.onRunService(props.id);
  }
}

function handleToggleDisabled(event: Event) {
  event.stopPropagation();
  if (props.data.onToggleDisabled) {
    props.data.onToggleDisabled(props.id);
  }
}

function handleDeleteService(event: Event) {
  event.stopPropagation();
  if (props.data.onDeleteService) {
    props.data.onDeleteService(props.id);
  }
}

function handleDuplicateService(event: Event) {
  event.stopPropagation();
  if (props.data.onDuplicateService) {
    props.data.onDuplicateService(props.id);
  }
}

function handleRenameService(event: Event) {
  event.stopPropagation();
  if (props.data.onRenameService) {
    props.data.onRenameService(props.id);
  }
}

function handleEditParents(event: Event) {
  event.stopPropagation();
  if (props.data.onEditParents) {
    props.data.onEditParents(props.id);
  }
}

function handleMouseEnter() {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }
  isHovered.value = true;
}

function handleMouseLeave() {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
}

</script>

<template>
  <div
    class="service-node card bb-builder-panel"
    :class="{ 'service-node-disabled': data.disabled }"
    :style="{ '--node-color': nodeColor }"
    @click="handleNodeClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Input handle (hidden for first node, invisible in history/run modes) -->
    <Handle
      v-if="!data.isFirstNode"
      type="target"
      :id="data.flowDirection === 'LR' ? 'left' : 'top'"
      :position="inputHandlePosition"
      :connectable="data.canvasMode === 'editor'"
      :class="[
        'node-handle node-handle-input',
        { 'invisible-handle': data.canvasMode !== 'editor' && data.canvasMode !== 'history' },
        { 'history-handle': data.canvasMode === 'history' },
        {
          'node-handle-input-top': inputHandlePosition === Position.Top,
          'node-handle-input-left': inputHandlePosition === Position.Left,
        },
      ]"
    />

    <!-- Node content -->
    <div class="card-body p-3">
      <!-- Icon and title row -->
      <div class="d-flex align-items-center gap-2 mb-2">
        <div class="service-icon bg-light-subtle border">
          <i :class="serviceIcon" class="service-node-icon text-secondary"></i>
        </div>
        <!-- text-break lets a name with no spaces break mid-word; pe-2 stops the text short of
             the status icon in the top-right corner. -->
        <h4 class="service-title mb-0 fw-semibold text-break pe-2" :title="data.label">
          {{ data.label }}
        </h4>
        <!-- Disabled indicator -->
        <div
          v-if="data.disabled && !nodeStatus"
          class="disabled-indicator"
        >
          <i class="bi bi-power text-danger" title="Service is disabled"></i>
        </div>
        <!-- Status indicator for history mode or completed execution -->
        <div
          v-else-if="nodeStatus"
          class="status-indicator"
          :title="getRunStatusLabel(nodeStatus)"
        >
          <i
            class="bi"
            :class="[
              statusClass,
              {
                'bi-check-circle': nodeStatus === 'Succeeded' || nodeStatus === 'success' || nodeStatus === 'completed',
                'bi-exclamation-triangle': nodeStatus === 'Warning' || nodeStatus === 'warning',
                'bi-x-circle': nodeStatus === 'Failed' || nodeStatus === 'error' || nodeStatus === 'failed',
                'bi-arrow-repeat': nodeStatus === 'Running' || nodeStatus === 'running',
                'bi-clock': nodeStatus === 'Pending' || nodeStatus === 'pending',
                'bi-dash-circle': nodeStatus === 'skipped',
              },
            ]"
          ></i>
        </div>
      </div>

      <!-- Permanent subtitle — service type label, always muted -->
      <div
        v-if="data.subtitle"
        class="service-description text-muted small"
      >
        {{ data.subtitle }}
      </div>

    </div>

    <!-- State badges, bottom-left: Component, and the number of validators on an HTTP kind. -->
    <div v-if="data.badges?.length" class="position-absolute bottom-0 start-0 ms-3 mb-2 d-flex gap-1">
      <span
        v-for="badge in data.badges"
        :key="badge.text"
        class="badge border bg-body text-body-secondary fw-normal d-inline-flex align-items-center gap-1"
      >
        <i :class="badge.icon" aria-hidden="true"></i>{{ badge.text }}
      </span>
    </div>

    <!-- Duration — bottom-right, history/run mode only -->
    <small
      v-if="runDuration"
      class="run-duration text-body-tertiary d-flex align-items-center"
    >
      <i class="bi bi-clock me-1"></i>{{ runDuration }}
    </small>

    <!-- Transaction counts — bottom-left, history mode only -->
    <div
      v-if="data.run"
      class="position-absolute start-0 bottom-0 ms-2 mb-1 d-flex align-items-center gap-1"
    >
      <StatusCountChips
        :chips="serviceCountChips(data.run)"
        size="sm"
        :climbing="isStillCounting(data.run)"
      />
      <span
        v-if="finishedWithNoTransactions(data.run)"
        class="badge rounded-pill bg-secondary small"
        title="No transactions"
      >0</span>
    </div>

    <!-- VueFlow NodeToolbar -->
    <NodeToolbar
      v-if="data.canvasMode === 'editor'"
      :is-visible="isHovered"
      :position="Position.Top"
      align="end"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div class="d-flex gap-1">
        <button
          class="toolbar-btn"
          title="Run service"
          @click="handleRunService"
        >
          <i class="bi bi-play-fill"></i>
        </button>
        <button
          class="toolbar-btn"
          :title="data.disabled ? 'Enable service' : 'Disable service'"
          @click="handleToggleDisabled"
        >
          <i
            class="bi bi-power"
            :class="data.disabled ? 'text-success' : 'text-danger'"
          ></i>
        </button>
        <button
          class="toolbar-btn toolbar-btn-danger"
          title="Delete service"
          @click="handleDeleteService"
        >
          <i class="bi bi-trash"></i>
        </button>

        <!-- Dropdown for more options -->
        <div class="dropdown">
          <button
            class="toolbar-btn dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            title="More options"
          >
            <i class="bi bi-three-dots"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-start">
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleRunService"
              >
                <i class="bi bi-play-fill me-2"></i>Run
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <!-- Canvas only shows the floating + on leaf nodes; this lets any service add a child, matching the list view. -->
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleAddService"
              >
                <i class="bi bi-plus-circle me-2"></i>Add Child Service
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleToggleDisabled"
              >
                <i
                  class="bi bi-power me-2"
                  :class="data.disabled ? 'text-success' : 'text-danger'"
                ></i>
                {{ data.disabled ? "Enable" : "Disable" }}
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleDuplicateService"
              >
                <i class="bi bi-files me-2"></i>Duplicate
              </a>
            </li>
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleRenameService"
              >
                <i class="bi bi-pencil me-2"></i>Rename
              </a>
            </li>
            <li>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="handleEditParents"
              >
                <i class="bi bi-diagram-3 me-2"></i>Edit Parent Services
              </a>
            </li>
            <li><hr class="dropdown-divider" /></li>
            <li>
              <a
                class="dropdown-item text-danger"
                href="#"
                @click.prevent="handleDeleteService"
              >
                <i class="bi bi-trash me-2"></i>Delete
              </a>
            </li>
          </ul>
        </div>
      </div>
    </NodeToolbar>

    <!-- Output handle - show for all nodes in editor mode (leaf nodes anchor the
         add-service connector there). In history mode the handle is a visible
         joining dot, so drop it on leaf nodes — nothing flows out, so a dangling
         dot would be misleading. -->
    <Handle
      v-if="!(data.canvasMode === 'history' && data.isLeafNode)"
      type="source"
      :id="data.flowDirection === 'LR' ? 'right' : 'bottom'"
      :position="outputHandlePosition"
      :connectable="data.canvasMode === 'editor'"
      :class="[
        'node-handle node-handle-output',
        { 'invisible-handle': data.canvasMode !== 'editor' && data.canvasMode !== 'history' },
        { 'history-handle': data.canvasMode === 'history' },
        {
          'node-handle-output-bottom': outputHandlePosition === Position.Bottom,
          'node-handle-output-right': outputHandlePosition === Position.Right,
        },
      ]"
    />

    <!-- Add service connector for leaf nodes in editor mode -->
    <div
      v-if="data.isLeafNode && data.canvasMode === 'editor'"
      :class="[
        'add-service-connector',
        {
          'add-service-connector-bottom':
            outputHandlePosition === Position.Bottom,
          'add-service-connector-right':
            outputHandlePosition === Position.Right,
        },
      ]"
    >
      <div
        :class="[
          'add-service-line',
          {
            'add-service-line-vertical':
              outputHandlePosition === Position.Bottom,
            'add-service-line-horizontal':
              outputHandlePosition === Position.Right,
          },
        ]"
        ref="addServiceLine"
      ></div>
      <div
        class="add-service-circle"
        @click="handleAddService"
        @mouseenter="
          () => {
            if (addServiceLine) addServiceLine.style.opacity = '1';
          }
        "
        @mouseleave="
          () => {
            if (addServiceLine) addServiceLine.style.opacity = '0.7';
          }
        "
      >
        <i class="bi bi-plus"></i>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.service-node {
  width: 240px !important;
  height: 120px !important;
  position: relative;
  transition: var(--bs-transition);
  cursor: pointer !important;
  box-sizing: border-box;
}

.service-node:hover {
  //transform: translateY(-2px);
  //z-index: 1;
}

/* Ensure dropdown menus in hovered nodes appear above other nodes */
.service-node:hover .dropdown-menu {
  //z-index: 1070 !important;
}

.service-node-disabled {
  opacity: 0.6;
  border-color: var(--bs-warning) !important;
  background-color: var(--bs-warning-bg-subtle) !important;
  /* Ensure no size changes */
  width: 240px !important;
  height: 120px !important;
  box-sizing: border-box;
}

/* The node has a fixed height, and a third title line would run into the subtitle. The title
   stops at two lines with an ellipsis; the full name is in its title attribute. */
.service-title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.service-node-disabled .service-title {
  color: var(--bs-warning-text-emphasis) !important;
}

.service-node-disabled .service-description {
  color: var(--bs-warning-text-emphasis) !important;
  opacity: 0.8;
}

.service-node-disabled .service-icon {
  border-color: var(--bs-warning-border-subtle) !important;
  background-color: var(--bs-warning-bg-subtle) !important;
}

.service-node-disabled .service-icon i {
  color: var(--bs-warning-text-emphasis) !important;
}

.disabled-indicator {
  font-size: 14px;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.service-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

/* Colour comes from the .text-secondary utility on the element (brand orange in this theme —
   see DESIGN-GUIDELINES.md), matching the stacked/list service icons. No colour set here so the
   utility class wins; a scoped rule would out-specify it and revert the icon to grey. */
.service-node-icon {
  line-height: 1;
}

[data-bs-theme="dark"] .service-icon {
  background-color: var(--bs-secondary-bg) !important;
  border-color: var(--bs-secondary-border-subtle) !important;
}


.status-indicator {
  font-size: 16px;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

/* Duration shown bottom-right of the fixed-size service node — kept visually
   quiet so the status icon remains the primary indicator. */
.run-duration {
  position: absolute;
  bottom: 6px;
  right: 8px;
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.85;
}

/* Small solid orange joining dot at each connection point — one look shared by the
   editor and history canvases, matching the list view's junction dots (var(--bs-warning)
   fill with a body-bg outline). The editor's are interactive (drag to connect), so they
   grow on hover to stay grabbable; history overrides that below. */
.node-handle {
  width: 9px;
  height: 9px;
  border: 1.5px solid var(--bs-body-bg);
  background: var(--bs-warning);
  border-radius: 50%;
  transition: var(--bs-transition);
}

.node-handle-input-top {
  top: -4.5px;
}

.node-handle-input-left {
  left: -4.5px;
}

.node-handle-output-bottom {
  bottom: -4.5px;
}

.node-handle-output-right {
  right: -4.5px;
}

.node-handle:hover {
  width: 14px;
  height: 14px;
}

/* History mode: read-only run, so the dot is static — no hover growth, no connect cursor. */
.history-handle {
  cursor: default;
}

.history-handle:hover {
  width: 9px;
  height: 9px;
}

/* VueFlow's own handle class lands on the same element and is defined after
   .node-handle, so keep it in step with the dot size or it would override back to
   the old size. Hover growth is owned by .node-handle:hover above. */
.vue-flow__handle {
  width: 9px;
  height: 9px;
}

.invisible-handle {
  opacity: 0 !important;
  pointer-events: none !important;
  width: 1px !important;
  height: 1px !important;
  border: none !important;
}

.invisible-handle.node-handle-input-top {
  top: 0px !important;
}

.invisible-handle.node-handle-input-left {
  left: 0px !important;
}

.invisible-handle.node-handle-output-bottom {
  bottom: 0px !important;
}

.invisible-handle.node-handle-output-right {
  right: 0px !important;
}

/* Base add-service-connector positioning */
.add-service-connector {
  position: absolute;
  z-index: 10;
  pointer-events: none; /* Allow events to pass through to handle */
}

/* Bottom positioning (TB flow) */
.add-service-connector-bottom {
  bottom: -58px;
  left: 50%;
  transform: translateX(-50%);
}

/* Right positioning (LR flow) */
.add-service-connector-right {
  right: -58px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  height: 24px; /* Match circle height for perfect alignment */
}

/* Base line styling — theme token, not a fixed hex, so it reads as the same grey as
   the canvas connectors (var(--bs-border-color)) and adapts to dark mode instead of
   looking near-white against the dark canvas. */
.add-service-line {
  background-color: var(--bs-border-color);
  opacity: 1;
}

/* Vertical line for TB flow */
.add-service-line-vertical {
  width: 1px;
  height: 22px;
  margin: -2px auto;
  margin-top: 12px;
}

/* Horizontal line for LR flow */
.add-service-line-horizontal {
  width: 22px;
  height: 1px;
  margin-left: 12px;
  /* Line will be centered by parent flexbox */
  flex-shrink: 0;
}

.add-service-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: var(--bs-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: var(--bs-transition);
  box-shadow: var(--bs-box-shadow-sm);
  cursor: pointer;
  pointer-events: auto; /* Re-enable pointer events only for the circle */
}

/* Add margin-top only for vertical (TB) layout */
.add-service-connector-bottom .add-service-circle {
  margin-top: 2px;
}

.add-service-circle:hover {
  transform: scale(1.1);
  box-shadow: var(--bs-box-shadow);
}

.toolbar-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius-sm);
  background: var(--bs-body-bg);
  color: var(--bs-secondary-color);
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--bs-transition);
  box-shadow: var(--bs-box-shadow-sm);
}

.toolbar-btn:hover {
  background: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  transform: scale(1.1);
  box-shadow: var(--bs-box-shadow);
}

.toolbar-btn:active {
  transform: scale(0.95);
}

.toolbar-btn-danger:hover {
  color: var(--bs-danger);
}

.dropdown-toggle::after {
  display: none;
}
</style>
